import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useEffect} from 'react';
import {storeMutationAdapter} from '../../../adapter/storeMutationAdapter';
import {checkIfContentHomeScheme} from '../../../common/checkIfContentHomeScheme';
import {DisplayAdContainer} from '../../../common/containers/DisplayAdContainer/DisplayAdContainer';
import {MyNewsContainerProps} from '../../../common/containers/MyNewsContainerProps.interface';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {twMerge} from '../../../common/twMerge';
import useTypedDispatch from '../../../common/useTypedDispatch';
import useTypedSelector from '../../../common/useTypedSelector';
import {Text} from '../../../design-system/atoms/Text/Text';
import {VerticalListWithDivider} from '../../../design-system/molecules/VerticalListWithDivider/VerticalListWithDivider';
import {NotificationItem} from '../../../design-system/organisms/NotificationItem/NotificationItem';
import {
  MyNewsQuery,
  MyNewsTabType,
  NotiType,
} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {ProfileMenuId} from '../../../profileMenu/constants';
import {setIsUpdated} from '../../../profileMenu/profileMenuSlice';
import {theme} from '../../../theme';
import {useMyNewsQuery} from '../../../wrapper';
import {MY_NEWS_TAB_LIST} from '../constant';
import {getAlarmTypeFromLogName} from '../util/getAlarmTypeFromLogName';
import {getIsNotiRemovable} from '../util/getIsNotiRemovable';
import {getNotiThumbnailType} from '../util/getNotiThumbnailType';
import {getUpdatedMyNewsItemIdList} from '../util/getUpdatedMyNewsItemIdList';
import {
  setAllNeedRefresh,
  setNeedRefresh,
  setUpdatedMyNewsIdList,
} from '../util/myNewsSlice';
import {storeMyNewsInfoToLocalStorage} from '../util/storeMyNewsInfoToLocalStorage';

export function MyNewsContainer({
  data,
  tabType,
  myNewsListInput,
  className,
  ...props
}: MyNewsContainerProps) {
  const {updatedMyNewsIdList, allTabNeedRefresh} = useTypedSelector(state => ({
    updatedMyNewsIdList: state.myNews.updatedMyNewsIdList,
    allTabNeedRefresh: state.myNews.all.needRefresh,
  }));

  const dispatch = useTypedDispatch();

  const {data: allTabData, isSuccess} = useMyNewsQuery(
    {
      myNewsListInput: {tab: MyNewsTabType.All, refresh: allTabNeedRefresh},
    },
    {enabled: false}
  );

  useEffect(() => {
    if (isSuccess) {
      // 전체 탭의 데이터가 변경될 때 한정으로 업데이트 된 알림 아이템 리스트 정보를 갱신합니다.
      dispatch(
        setUpdatedMyNewsIdList({
          updatedMyNewsIdList: getUpdatedMyNewsItemIdList({
            myNews: allTabData?.myNewsList?.news,
          }),
        })
      );
      // 전체 탭의 데이터가 변경될 때 한정으로 Local Storage에 알림 관련 정보를 저장합니다.
      // 가장 최신 알림 아이템 리스트와, 가장 최신 알림의 날짜 정보를 저장합니다.
      storeMyNewsInfoToLocalStorage({myNews: allTabData?.myNewsList?.news});
      dispatch(setIsUpdated({menuId: ProfileMenuId.Alarm, isUpdated: false}));
    }
  }, [allTabData, isSuccess, dispatch]);

  const queryClient = useQueryClient();

  const {mutate: removeMyNewsItem} = useMutation(
    storeMutationAdapter.removeMyNews()
  );

  if (!data) {
    return null;
  }

  const hasNewsItems = data.news && data.news.length > 0;

  return (
    <div
      className={twMerge('flex grow flex-col px-15pxr', className)}
      {...props}
    >
      {data.displayAd && requestContext.requestInfo.display.isMobile && (
        <DisplayAdContainer displayAd={data.displayAd} className="mt-15pxr" />
      )}
      {!hasNewsItems && (
        <div className="flex-center-full">
          <Text className="font-medium1 text-theme-solid-100">
            {t('notification_null')}
          </Text>
        </div>
      )}
      {hasNewsItems && (
        <VerticalListWithDivider
          dividerColor={theme.colors['theme-transparent-10']}
        >
          {data.news?.map((newsItem, idx) => {
            const notiThumbnailType = getNotiThumbnailType(newsItem.logName);
            const {isContentHome, seriesId} = checkIfContentHomeScheme(
              newsItem.scheme
            );
            return (
              <NotificationItem
                key={newsItem.newsId}
                title={newsItem.title || ''}
                message={newsItem.message || ''}
                thumbnail={newsItem.thumbnail}
                date={new Date(newsItem.date)}
                thumbnailType={notiThumbnailType}
                canRemove={
                  newsItem.logName
                    ? getIsNotiRemovable(newsItem.logName)
                    : false
                }
                hasDot={updatedMyNewsIdList.includes(newsItem.newsId)}
                scheme={newsItem.scheme}
                onClickRemove={e =>
                  LOG_CLICK_EVENT('삭제_클릭', e, () =>
                    removeMyNewsItem(
                      {input: {newsId: newsItem.newsId}},
                      {
                        onSuccess: (result, variables) => {
                          if (!result?.removeMyNews.newsId) {
                            alert('newsId가 존재하지 않습니다.');
                            return;
                          }

                          const targetIdx = data?.news?.findIndex(
                            newsItem =>
                              newsItem.newsId === variables.input.newsId
                          );

                          if (targetIdx !== undefined && targetIdx !== -1) {
                            queryClient.setQueryData<MyNewsQuery>(
                              // FIXME: @steve GraphQL 응답 타입과 Adapter 응답 타입. GraphQL 제거되면 MyNewsResultVo 만 남기도록 합니다.
                              useMyNewsQuery.getKey({
                                myNewsListInput,
                              }),
                              oldData => {
                                if (!oldData) {
                                  return oldData;
                                }

                                const _oldData = oldData;
                                if (!_oldData.myNewsList?.news) {
                                  return oldData;
                                }
                                const newNewsData = [
                                  ..._oldData.myNewsList.news,
                                ];
                                newNewsData.splice(targetIdx, 1);
                                return {
                                  ..._oldData,
                                  myNewsList: {
                                    ..._oldData?.myNewsList,
                                    news: newNewsData,
                                  },
                                };
                              }
                            );
                          }

                          if (tabType === MyNewsTabType.All) {
                            dispatch(setAllNeedRefresh());
                          } else {
                            dispatch(
                              setNeedRefresh({tabType, needRefresh: true})
                            );
                          }
                        },
                      }
                    )
                  )
                }
                onClickNotificationItem={e => {
                  aggregateAndSendLog({
                    targetElem: e.target as HTMLElement,
                    actionType: 'Event',
                    actionName: `${isContentHome ? '작품' : '알림'}_클릭`,
                    eventMeta: {
                      id: isContentHome
                        ? seriesId
                        : (newsItem.scheme as string),
                      type: isContentHome ? 'seriesId' : 'scheme',
                      name: newsItem.title,
                      // 로그정의서 상으로는 카테고리 및 서브카테고리 정보를 넣어야 한다고 명시되어 있으나,
                      // 알림 API에서는 관련 정보를 주지 않으며 스킴을 파싱해도 알 수 있는 정보는 seriesId 뿐이므로
                      // 해당 정보들에 undefined를 할당합니다. (기획과 논의 후 반영)
                      category: undefined,
                      subcategory: undefined,
                      provider: '서비스',
                      series: undefined,
                      series_id: seriesId,
                    },
                    customProps: {
                      user_action: 'click',
                    },
                    click: {
                      ordnum: String(idx + 1),
                    },
                  });
                }}
                {...putTiaraObjectProps({
                  click: {
                    layer1: `섹션_${
                      MY_NEWS_TAB_LIST.find(tabItem => tabItem.id === tabType)
                        ?.text
                    }`,
                  },
                  customProps: {
                    event_comment_id: String(newsItem.newsId),
                    alarm_type: getAlarmTypeFromLogName(
                      newsItem.logName as NotiType
                    ),
                  },
                })}
              />
            );
          })}
        </VerticalListWithDivider>
      )}
    </div>
  );
}
